import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { colorsSemantic } from "../../theme/tokens/colors-semantic.js";
import { hexToRgba } from "../../theme/utils/colors.js";

const ACTION_LABELS = {
  pruefen: "Prüfen",
  bearbeiten: "Bearbeiten",
  nachfassen: "Nachfassen",
};

/**
 * One claim on the "Meine Aufgaben" board.
 *
 * Note there is deliberately no status pill: a claim in `pre_check` displays as
 * "In Prüfung" while the ball is with us, which contradicts its "Wir bearbeiten"
 * wait badge. The wait badge is the truthful label; the raw status is available
 * on the row's tooltip.
 */
export default function TaskRow({ task, onOpen }) {
  const { attention } = colorsSemantic;

  const waitColor = attention.waiting[task.waiting_on] ?? attention.waiting.makler;
  const urgent = task.is_new || task.overdue;
  const flagTone = task.is_new ? attention.flag.neu : attention.flag.overdue;
  const flagText = task.is_new ? "NEU" : task.overdue ? "ÜBERFÄLLIG" : "";

  // New claims get the primary treatment — they are the ones nobody has looked at.
  const primaryAction = task.is_new;

  const open = () => onOpen(task);

  return (
    <Box
      onClick={open}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "11px",
        padding: "11px 18px",
        borderBottom: `1px solid ${colorsSemantic.border.hairline}`,
        cursor: "pointer",
        "&:hover": { backgroundColor: colorsSemantic.surface.rowHover },
      }}
    >
      {/* Urgency rail */}
      <Box
        sx={{
          width: "3px",
          alignSelf: "stretch",
          minHeight: "34px",
          borderRadius: "3px",
          flex: "none",
          backgroundColor: task.is_new
            ? attention.overdue
            : task.overdue
              ? attention.waiting.kunde
              : waitColor,
        }}
      />

      <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          {urgent && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 7px",
                borderRadius: "5px",
                fontSize: "9.5px",
                fontWeight: 800,
                letterSpacing: ".04em",
                flex: "none",
                backgroundColor: flagTone.bg,
                color: flagTone.fg,
              }}
            >
              {flagText}
            </Box>
          )}
          <Typography
            title={`${task.title} — ${task.status_label}`}
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {task.title}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "11.5px",
            color: colorsSemantic.text.muted,
            mt: "2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {[task.code, task.sub].filter(Boolean).join(" · ")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap", mt: "7px" }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "3px 9px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: 700,
              whiteSpace: "nowrap",
              flex: "none",
              backgroundColor: hexToRgba(waitColor, 0.1),
              color: waitColor,
            }}
          >
            <Box
              component="span"
              sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: waitColor, flex: "none" }}
            />
            {task.waiting_on_label}
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: urgent ? "#b91c1c" : colorsSemantic.text.muted,
            }}
          >
            {task.is_new
              ? `vor ${task.days} Tg. eingegangen`
              : `seit ${task.days} Tg. offen`}
          </Typography>
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        onClick={(event) => {
          // The whole row is already the click target; keep the button from
          // triggering it a second time.
          event.stopPropagation();
          open();
        }}
        sx={{
          flex: "none",
          mt: "1px",
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: "pointer",
          border: primaryAction ? "none" : `1.5px solid ${colorsSemantic.border.card}`,
          backgroundColor: primaryAction ? "#1F8A5B" : colorsSemantic.background.paper,
          color: primaryAction ? colorsSemantic.text.inverse : colorsSemantic.text.accent.blue,
        }}
      >
        {ACTION_LABELS[task.action] ?? ACTION_LABELS.bearbeiten}
      </Box>
    </Box>
  );
}

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    title: PropTypes.string,
    sub: PropTypes.string,
    status_label: PropTypes.string,
    waiting_on: PropTypes.string,
    waiting_on_label: PropTypes.string,
    days: PropTypes.number,
    overdue: PropTypes.bool,
    is_new: PropTypes.bool,
    action: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};
